import {
  ContactGroupIcon,
  PhoneIcon,
  MailIcon,
  DeleteIcon,
  AddIcon,
} from "../../../editorjs/icons/icons";

import BtnTooltip from "../../tooltips/btnTooltip";

const PHONE_ALLOWED_CHARS = /[^\d+\-\s()]/g;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default class ContactGroup {
  static get toolbox() {
    return {
      icon: ContactGroupIcon,
      title: "Contact group",
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly, block }) {
    this.api = api;
    this.readOnly = readOnly;
    this.block = block;

    this._CSS = {
      baseClass: this.api.styles.block,
      loading: this.api.styles.loader,
      settingsButton: this.api.styles.settingsButton,
      settingsButtonActive: this.api.styles.settingsButtonActive,

      /**
       * Tool's classes
       */
      mainQuestionContainer: "main-question-container",
      questionInput: "question-input",

      typeContainer: "question-type-container",
      typeLabel: "question-type-label",
      typeSelect: "question-type-select",
      typeOption: "question-type-option",

      optionsContainer: "question-options-container",
      addOptionBtn: "add-option-btn",

      optionItem: "question-option-item",
      optionInput: "question-option-input",
      removeOptionBtn: "remove-option-btn",
      correctOptionCheckbox: "correct-option-checkbox",

      blockBtn: "block-btn",
      blockBtnSmall: "small",
      blockBtnLarge: "large",
      largeIcon: "large-icon",
      xlIcon: "xl-icon",
      xxlIcon: "xxl-icon",
      clear: "clear",
      focusVisibleBtn: "focus-visible-btn",
      tuneWrapper: "editor-block-tune",
      tuneTitle: "editor-block-tune-title",
      tuneText: "editor-block-tune-text",
      tuneInput: "editor-block-tune-input",
      editorFont: "editor-font",
      editorBlockModal: "editor-block-modal",
      editorBlockModalTitle: "editor-block-modal-title",
      editorBlockModalLabel: "editor-block-modal-label",
      editorBlockModalText: "editor-block-modal-text",
      editorBlockModalInput: "editor-block-modal-input",
      adminFont: "admin-font",

      /**
       * Contact group classes
       */
      contactGroupContainer: "contact-group-container",
      contactGroupTitle: "contact-group-title",

      phoneNumbersContainerMain: "phone-numbers-container-main",
      phoneNumbersContainer: "phone-numbers-container",
      phoneRow: "phone-row",
      phoneIconContainer: "phone-icon-container",
      phoneNumberInput: "phone-number-input",
      addPhoneBtn: "add-phone-btn",

      emailsContainerMain: "emails-container-main",
      emailsContainer: "emails-container",
      emailRow: "email-row",
      emailIconContainer: "email-icon-container",
      emailInput: "email-input",
      addEmailBtn: "add-email-btn",

      removeRowBtn: "remove-row-btn",
      invalidInput: "contact-group-input-invalid",
    };

    /**
     * Tool's settings passed from Editor
     *
     * @type {TitleConfig}
     * @private
     */
    this._settings = config;

    this._data = {
      contactGroupTitle: data.contactName || "",
      phoneNumbers: data.phoneNumbers || [""],
      emails: data.emails || [""],
    };
    this._container = "";

    this.contactGroupTitleEl = null;
    this.phoneNumbersContainer = null;
    this.emailsContainer = null;
  }

  /**
   * Strips disallowed characters from a contenteditable phone input,
   * preserving caret position when a correction is made.
   */
  sanitizePhoneInput(el) {
    const original = el.innerText;
    const sanitized = original.replace(PHONE_ALLOWED_CHARS, "");

    if (sanitized === original) {
      return;
    }

    const selection = window.getSelection();
    const caretOffset = selection?.anchorOffset ?? sanitized.length;

    el.innerText = sanitized;

    const range = document.createRange();
    const textNode = el.firstChild || el;
    const safeOffset = Math.min(caretOffset, sanitized.length);

    try {
      range.setStart(textNode, safeOffset);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (e) {
      // If the caret can't be restored precisely, leave it at the end
    }
  }

  /**
   * Flags an email input as invalid (visually) if it doesn't match a
   * basic email pattern. Empty values are not flagged.
   */
  validateEmailInput(el) {
    const value = el.innerText.trim();

    if (!value || EMAIL_PATTERN.test(value)) {
      el.classList.remove(this._CSS.invalidInput);
    } else {
      el.classList.add(this._CSS.invalidInput);
    }
  }

  /**
   * Builds a remove button for a row, styled the same as Question's
   * removeOptionBtn.
   */
  removeRowBtn(onRemove) {
    const css = this._CSS;
    const btn = document.createElement("button");

    btn.classList.add(
      css.blockBtn,
      css.blockBtnSmall,
      css.xlIcon,
      css.clear,
      css.removeRowBtn,
    );
    btn.innerHTML = DeleteIcon;
    BtnTooltip(btn, "Remove this entry");

    if (this.readOnly) {
      btn.setAttribute("disabled", "disabled");
    } else {
      btn.addEventListener("click", onRemove);
    }

    return btn;
  }

  phoneRow(value) {
    const css = this._CSS;
    const row = document.createElement("div");
    const phoneIconContainer = document.createElement("div");
    const phoneNumber = document.createElement("p");

    row.classList.add(css.phoneRow);
    phoneIconContainer.classList.add(css.phoneIconContainer);
    phoneNumber.classList.add(css.phoneNumberInput);
    phoneNumber.setAttribute("data-placeholder", "Enter phone number");
    phoneIconContainer.innerHTML = PhoneIcon;

    if (value) {
      phoneNumber.innerText = value;
    }

    row.appendChild(phoneIconContainer);
    row.appendChild(phoneNumber);

    if (!this.readOnly) {
      phoneNumber.setAttribute("contenteditable", "true");
      phoneNumber.addEventListener("input", () => {
        this.sanitizePhoneInput(phoneNumber);
      });
      phoneNumber.addEventListener("paste", (e) => {
        e.preventDefault();
        const text = (e.clipboardData || window.clipboardData).getData("text");
        document.execCommand(
          "insertText",
          false,
          text.replace(PHONE_ALLOWED_CHARS, ""),
        );
      });

      row.appendChild(
        this.removeRowBtn(() => {
          row.remove();
        }),
      );
    }

    return row;
  }

  emailRow(value) {
    const css = this._CSS;
    const row = document.createElement("div");
    const emailIconContainer = document.createElement("div");
    const email = document.createElement("p");

    row.classList.add(css.emailRow);
    emailIconContainer.classList.add(css.emailIconContainer);
    email.classList.add(css.emailInput);
    email.setAttribute("data-placeholder", "Enter email");
    emailIconContainer.innerHTML = MailIcon;

    if (value) {
      email.innerText = value;
    }

    row.appendChild(emailIconContainer);
    row.appendChild(email);

    if (!this.readOnly) {
      email.setAttribute("contenteditable", "true");
      email.addEventListener("blur", () => {
        this.validateEmailInput(email);
      });

      row.appendChild(
        this.removeRowBtn(() => {
          row.remove();
        }),
      );
    }

    return row;
  }

  phoneNumbers() {
    const css = this._CSS;
    const phoneNumbersContainerMain = document.createElement("div");
    const addBtn = document.createElement("button");

    phoneNumbersContainerMain.classList.add(css.phoneNumbersContainerMain);

    this.phoneNumbersContainer = document.createElement("div");
    this.phoneNumbersContainer.classList.add(css.phoneNumbersContainer);

    addBtn.innerHTML = `${AddIcon} Add phone number`;
    addBtn.classList.add(css.blockBtn, css.blockBtnLarge, css.addPhoneBtn);
    addBtn.addEventListener("click", () => {
      this.phoneNumbersContainer.appendChild(this.phoneRow());
    });

    this._data.phoneNumbers.forEach((item) => {
      this.phoneNumbersContainer.appendChild(this.phoneRow(item));
    });

    phoneNumbersContainerMain.appendChild(this.phoneNumbersContainer);
    if (!this.readOnly) {
      phoneNumbersContainerMain.appendChild(addBtn);
    }

    return phoneNumbersContainerMain;
  }

  emails() {
    const css = this._CSS;
    const emailsContainerMain = document.createElement("div");
    const addBtn = document.createElement("button");

    emailsContainerMain.classList.add(css.emailsContainerMain);

    this.emailsContainer = document.createElement("div");
    this.emailsContainer.classList.add(css.emailsContainer);

    addBtn.innerHTML = `${AddIcon} Add email`;
    addBtn.classList.add(css.blockBtn, css.blockBtnLarge, css.addEmailBtn);
    addBtn.addEventListener("click", () => {
      this.emailsContainer.appendChild(this.emailRow());
    });

    this._data.emails.forEach((item) => {
      this.emailsContainer.appendChild(this.emailRow(item));
    });

    emailsContainerMain.appendChild(this.emailsContainer);
    if (!this.readOnly) {
      emailsContainerMain.appendChild(addBtn);
    }

    return emailsContainerMain;
  }

  contactGroupMain() {
    const css = this._CSS;
    const contactGroupContainer = document.createElement("div");

    contactGroupContainer.classList.add(css.contactGroupContainer);

    this.contactGroupTitleEl = document.createElement("h5");
    this.contactGroupTitleEl.classList.add(css.contactGroupTitle);
    this.contactGroupTitleEl.innerText = this._data.contactGroupTitle;

    if (!this.readOnly) {
      this.contactGroupTitleEl.setAttribute("contenteditable", "true");
    }

    contactGroupContainer.appendChild(this.contactGroupTitleEl);
    contactGroupContainer.appendChild(this.phoneNumbers());
    contactGroupContainer.appendChild(this.emails());

    return contactGroupContainer;
  }

  render() {
    this._container = document.createElement("div");
    this._container.appendChild(this.contactGroupMain());
    return this._container;
  }

  save() {
    const contactGroupTitle = this.contactGroupTitleEl
      ? this.contactGroupTitleEl.innerText.trim()
      : "";

    let phoneNumbers = Array.from(
      this.phoneNumbersContainer.querySelectorAll(
        `.${this._CSS.phoneNumberInput}`,
      ),
    )
      .map((el) => el.innerText.trim())
      .filter((value) => value.length > 0);

    let emails = Array.from(
      this.emailsContainer.querySelectorAll(`.${this._CSS.emailInput}`),
    )
      .map((el) => el.innerText.trim())
      .filter((value) => value.length > 0);

    if (phoneNumbers.length === 0) {
      phoneNumbers = [""];
    }
    if (emails.length === 0) {
      emails = [""];
    }

    return {
      contactName: contactGroupTitle,
      phoneNumbers,
      emails,
    };
  }
}
